import { useState } from "react";
import { SquadDTO } from "@/types/squad.type";
import { SquadFiltrosCriteria, SquadFiltrosOptions } from "@workflow/types";

import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { squadQueries, squadKeys } from "@/services/api/queries/squad.queries";
import { useSquadActions } from "@/hooks/squads/use-squad-actions";

// Tipagem auxiliar para padronizar nossos modais
interface ModalState<T> {
  open: boolean;
  data: T | null;
}

export function useSquadTable() {
  // Camada de dados padronizada. `useSquadActions` já invalida `squads-list`,
  // então o toggle de status refresca a lista sozinho (sem refetch manual).
  const { tableProps, isError } = useServerDataTable<
    SquadDTO,
    SquadFiltrosOptions,
    SquadFiltrosCriteria
  >({
    queryOptions: squadQueries.list,
    filters: squadQueries.filtros,
    invalidateKey: squadKeys.list,
  });

  const { inactivateSquad, reactivateSquad, isTogglingStatus } = useSquadActions();

  // Agrupando estados de modal para evitar o "useState hell"
  const [statusModal, setStatusModal] = useState<ModalState<SquadDTO>>({ open: false, data: null });
  const [editModal, setEditModal] = useState<ModalState<SquadDTO>>({ open: false, data: null });
  const [viewModal, setViewModal] = useState<ModalState<SquadDTO>>({ open: false, data: null });

  // Handler de Negócio
  const handleConfirmStatusToggle = async () => {
    const squad = statusModal.data;
    if (!squad) return;

    try {
      if (squad.ativo) {
        await inactivateSquad(squad.id);
      } else {
        await reactivateSquad(squad.id);
      }
    } finally {
      setStatusModal({ open: false, data: null });
    }
  };

  return {
    // Espalhe em `<DataTable {...tableProps} columns ... />`.
    tableProps,
    isError,
    isTogglingStatus,
    handleConfirmStatusToggle,
    modals: {
      status: {
        ...statusModal,
        openModal: (data: SquadDTO) => setStatusModal({ open: true, data }),
        setOpen: (open: boolean) => setStatusModal((prev) => ({ ...prev, open })),
      },
      edit: {
        ...editModal,
        openModal: (data: SquadDTO) => setEditModal({ open: true, data }),
        setOpen: (open: boolean) => setEditModal((prev) => ({ ...prev, open })),
      },
      view: {
        ...viewModal,
        openModal: (data: SquadDTO) => setViewModal({ open: true, data }),
        setOpen: (open: boolean) => setViewModal((prev) => ({ ...prev, open })),
      },
    },
  };
}
